import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { getFirst$$ } from '../../util/util';
import { first } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RestService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Helper method for a GET request.
   * @param url
   * @param isPublicCall
   * @param isAssetCall
   * @param customerId
   * @returns {Observable|"../../../Observable".Observable|"../../Observable".Observable}
   */
  public get<T>(url, isPublicCall = false, isAssetCall = false, customerId?: string): Observable<T> {
    return new Observable(observer => {
      const handleError = (error: any) => {
        observer.error(error);
        observer.complete();
      };
      this.getData<T>(url, isPublicCall, isAssetCall, customerId).subscribe({
        next: response => {
          observer.next(response);
          observer.complete();
        },
        error: error => {
          if (error) {
            //} && (!error.url.endsWith('/User/context') || error.status !== 401 || !this.adalService.triggerGetTokenForAcceptance(handleError, errorMessage))) {
            handleError(error);
          }
        },
      });
    });
  }

  async get$$<T>(url: string, type: 'asset' | 'public' | 'private' = 'private', customerId?: string): Promise<T> {
    try {
      return await this.getData$$<T>(url, type, customerId);
    } catch (error) {
      const handleError = error => {
        throw error;
      };
      const errorMessage = error.toString();
      if (error) {
        // } && (!error.url.endsWith('/User/context') || error.status !== 401 || !this.adalService.triggerGetTokenForAcceptance(handleError, errorMessage))) {
        handleError(error);
      }
    }
  }

  async getData$$<T>(url: string, type: 'asset' | 'public' | 'private', customerId?: string): Promise<T> {
    let result: T;
    switch (type) {
      case 'asset':
        result = await getFirst$$(this.httpClient.get<T>(`/assets${url}`, { headers: RestService.addCustomerToHeaders(customerId) }));
        break;
      case 'public':
      case 'private':
        result = await getFirst$$(this.httpClient.get<T>(`/api${url}`, { headers: RestService.addCustomerToHeaders(customerId) }));
        break;
      /*
            case 'private':
              const { token } = await this.adalService.acquireToken$$();
              const headers = new HttpHeaders({
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              });
              if (customerId || this._customerId) {
                headers.append('CustomerId', customerId || this._customerId);
              }
              result = await getFirst$$(this.httpClient.get<T>(`/api${url}`, { headers }));
              break;
      */
    }
    return result;
  }

  /**
   * Get blob data from middleware
   * method signatures is identical to http.get method.
   *
   *
   * @param url
   * @param postBody if not null/undefined, will do a post instead of a get
   * @param publicCall true if accessible without login
   * @param customerId
   * @returns {any}
   */
  getBlobData(url: string, postBody: object = null, publicCall: boolean = false, customerId?: string): Observable<Blob> {
    if (publicCall) {
      return this.httpClient.get(`/api${url}`, { responseType: 'blob', headers: RestService.addCustomerToHeaders(customerId) });
    }
    return this.getDataOfType('blob', url, postBody, customerId);
  }

  /**
   * Get text data from middleware
   * method signatures is identical to http.get method.
   *
   * @param url
   * @param publicCall true if accessible without login
   * @param customerId
   * @returns {any}
   */
  getTextData(url: string, publicCall: boolean = false, customerId?: string): Observable<string> {
    if (publicCall) {
      return this.httpClient.get(`/api${url}`, { responseType: 'text', headers: RestService.addCustomerToHeaders(customerId) });
    } else {
      return this.getDataOfType('text', url, undefined, customerId);
    }
  }

  /**
   * Based on this article: http://www.bentedder.com/upload-images-angular-4-without-plugin/
   * @param {String} url
   * @param body
   * @param customerId
   * @returns {Observable<Response>}
   */
  public upload(url: String, body: FormData, customerId?: string): Observable<Response> {
    return new Observable(observer => {
      this.httpClient
        .request('post', `/api${url}`, {
          body,
          headers: RestService.addCustomerToHeaders(customerId),
        })
        .pipe(first())
        .subscribe({
          next: (response: Response) => {
            observer.next(response);
            observer.complete();
          },
          error: err => {
            observer.error(err);
            observer.complete();
          },
        });
    });
  }

  /**
   * Stub taken from http interface, to be elaborated
   * @param url
   * @param body
   * @param customerId: if null, will take selected customerId
   * @param returnBody default returns body from response
   * @returns {Observable<Response>}
   * @private
   */
  // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars
  post<T>(url: string, body: any, customerId?: string, returnBody = true): Observable<T> {
    const subject = new Subject<any>();
    this.httpClient
      .post(`/api${url}`, body, { observe: 'response', headers: RestService.addCustomerToHeaders(customerId) })
      .pipe(first())
      .subscribe({
        next: response => {
          subject.next(returnBody ? response.body : response);
          subject.complete();
        },
        error: err => {
          subject.error(err);
        },
      });
    return subject.asObservable();
  }

  /**
   * Get data from middleware
   * method signatures is identical to http.get method.
   *
   * @param url
   * @param isPublicRequest true if accessible without login
   * @param isAssetRequest
   * @param customerId
   * @returns {any}
   * @private only used internally.  Use protected 'get' method in subclasses.
   */
  private getData<T>(url: string, isPublicRequest: boolean = false, isAssetRequest = false, customerId?: string): Observable<T> {
    if (isPublicRequest) {
      return this.httpClient.get<T>(isAssetRequest ? `/assets${url}` : `/api${url}`, { headers: RestService.addCustomerToHeaders(customerId) });
    } else {
      return new Observable(observer => {
        this.httpClient
          .get<T>(`/api${url}`, { headers: RestService.addCustomerToHeaders(customerId) })
          .pipe(first())
          .subscribe({
            next: response => {
              observer.next(response);
              observer.complete();
            },
            error: error => observer.error(error),
          });
      });
    }
  }

  private getDataOfType(responseType: 'text', url: string, postBody?: object, customerId?: string): Observable<string>;
  private getDataOfType(responseType: 'blob', url: string, postBody?: object, customerId?: string): Observable<Blob>;
  private getDataOfType(responseType: 'text' | 'blob', url: string, postBody?: object, customerId?: string): Observable<Blob | string> {
    return new Observable(observer => {
      const options = { responseType: responseType as 'json', headers: RestService.addCustomerToHeaders(customerId) };
      let apiCall;
      if (postBody) {
        apiCall = this.httpClient.post.bind(this.httpClient, `/api${url}`, postBody, options);
      } else {
        apiCall = this.httpClient.get.bind(this.httpClient, `/api${url}`, options);
      }
      apiCall()
        .pipe(first())
        .subscribe({
          next: (response: string) => {
            observer.next(response);
            observer.complete();
          },
          error: error => {
            try {
              const reader = new FileReader();
              reader.onload = function () {
                try {
                  return observer.error(JSON.parse(reader.result as string));
                } catch (e) {
                  return observer.error(error);
                }
              };
              reader.readAsText(error.message);
            } catch (e) {
              return observer.error(error);
            }
          },
        });
    });
  }

  /**
   * headers can't contain empty values. it should be a string or it shouldn't be included. this method will only add CustomerId to header if it's a non-empty string
   * @param customerId
   * @param headers
   * @private
   */
  private static addCustomerToHeaders(customerId: string | null | undefined, headers?: { [header: string]: string }): { [header: string]: string } {
    let headersObject = headers || {};
    if (customerId) {
      headersObject = { ...headersObject, CustomerId: customerId };
    }
    return headersObject;
  }
}
